import {alpha, Components, Theme} from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import {buttonClasses} from '@mui/material';

export const Button = (
  theme: Theme
): {
  MuiButton: Components['MuiButton'];
  MuiButtonBase: Components['MuiButtonBase'];
  MuiIconButton: Components['MuiIconButton'];
} => {
  return {
    MuiButtonBase: {
      defaultProps: {disableRipple: true},
    },
    MuiButton: {
      defaultProps: {disableRipple: true, loadingIndicator: <CircularProgress size='1em' color='inherit' />},
      variants: [
        {
          props: {color: 'secondary', variant: 'text'},
          style: {
            color: theme.palette.text.primary,
            backgroundColor: 'transparent',

            ':hover': {
              backgroundColor: theme.palette.action.hover,
            },
          },
        },
        {
          props: {
            variant: 'action',
          },
          style: {
            background: theme.palette.text.primary,
            border: `1px solid ${theme.palette.text.primary}`,
            color: theme.palette.common.white,

            ':hover': {
              background: 'transparent',
              color: theme.palette.text.primary,
            },
          },
        },
        {
          props: {
            variant: 'contained',
            color: 'info',
          },
          style: {
            color: theme.palette.common.black,

            background: theme.palette.background.white,

            ':hover': {
              background: alpha(theme.palette.common.black, theme.palette.action.hoverOpacity),
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          display: 'inline-grid',
          gridAutoFlow: 'column',
          gap: theme.spacing(2),
          borderRadius: theme.shape.borderRadiusRegular,
          boxShadow: 'none',

          ':hover': {
            boxShadow: 'none',
          },
        },

        outlinedSizeSmall: {
          padding: theme.spacing(1.75),
          borderColor: theme.palette.common.black,
          color: theme.palette.common.black,

          ':hover': {
            borderColor: theme.palette.common.black,
            backgroundColor: alpha(theme.palette.common.black, theme.palette.action.hoverOpacity),
          },
        },
        outlinedSizeMedium: {
          padding: theme.spacing(2.75),
        },
        outlinedSizeLarge: {
          padding: theme.spacing(3.75),
        },

        sizeSmall: {
          ...theme.typography.buttonSmall,
          padding: theme.spacing(2),
          minWidth: 32,

          [`& svg, > svg`]: {
            width: 16,
            height: 16,
          },

          [`& .${buttonClasses.loadingIndicator}`]: {
            fontSize: 16,
          },
        },
        sizeMedium: {
          ...theme.typography.buttonMedium,
          padding: theme.spacing(3),
          minWidth: 44,

          [`& svg, > svg`]: {
            width: 20,
            height: 20,
          },

          [`& .${buttonClasses.loadingIndicator}`]: {
            fontSize: 20,
          },
        },
        sizeLarge: {
          ...theme.typography.buttonLarge,
          padding: theme.spacing(4),
          minWidth: 56,

          [`& svg, > svg`]: {
            width: 24,
            height: 24,
          },

          [`& .${buttonClasses.loadingIndicator}`]: {
            fontSize: 24,
          },
        },
        iconSizeSmall: {margin: 'unset'},
        iconSizeMedium: {margin: 'unset'},
        iconSizeLarge: {margin: 'unset'},
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadiusRegular,
        },
        colorSecondary: {
          color: theme.palette.text.primary,
          backgroundColor: 'transparent',

          ':hover': {
            backgroundColor: theme.palette.action.hover,
          },
        },
        colorInfo: {
          color: theme.palette.icon.primary,
          background: theme.palette.background.white,

          ':hover': {
            background: alpha(theme.palette.common.black, 0.04),
          },
        },
        sizeSmall: {
          padding: theme.spacing(1),
        },
        sizeMedium: {
          padding: theme.spacing(3),
        },
        sizeLarge: {
          padding: theme.spacing(4),
        },
      },
    },
  };
};
